import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save, Image } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminKitchenware() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    reference: '',
    name_en: '',
    name_fr: '',
    dimensions: '',
    description_en: '',
    description_fr: '',
    image: '',
    active: true,
    order: 0
  });

  const auth = localStorage.getItem('adminAuth');
  const config = { headers: { Authorization: `Basic ${auth}` } };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/kitchenware`, config);
      setProducts(res.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {...formData};
      if (!payload.dimensions) payload.dimensions = null;
      
      if (editingProduct) {
        await axios.put(`${API}/admin/kitchenware/${editingProduct.id}`, payload, config);
      } else {
        await axios.post(`${API}/admin/kitchenware`, payload, config);
      }
      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (err) {
      console.error('Error saving product:', err);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      reference: product.reference,
      name_en: product.name_en,
      name_fr: product.name_fr,
      dimensions: product.dimensions || '',
      description_en: product.description_en,
      description_fr: product.description_fr,
      image: product.image,
      active: product.active,
      order: product.order
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit?')) return;
    try {
      await axios.delete(`${API}/admin/kitchenware/${id}`, config);
      fetchProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      reference: '',
      name_en: '',
      name_fr: '',
      dimensions: '',
      description_en: '',
      description_fr: '',
      image: '',
      active: true,
      order: 0
    });
  };

  const openAddModal = () => {
    setEditingProduct(null);
    resetForm();
    setShowModal(true);
  };

  const initDefaults = async () => {
    try {
      await axios.post(`${API}/admin/init-products`, {}, config);
      fetchProducts();
    } catch (err) {
      console.error('Error initializing:', err);
    }
  };

  if (loading) {
    return <div className="text-text-secondary">Chargement...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-playfair text-3xl text-text-primary">Articles Bois d'Olivier</h1>
          <p className="text-text-secondary mt-2">Gérer les articles en bois d'olivier</p>
        </div>
        <div className="flex gap-3">
          {products.length === 0 && (
            <button onClick={initDefaults} className="btn-secondary text-sm">
              Charger défauts
            </button>
          )}
          <button onClick={openAddModal} className="btn-primary inline-flex items-center gap-2">
            <Plus size={18} />
            Ajouter
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-surface-secondary border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-text-secondary text-sm font-normal">Image</th>
                <th className="text-left p-4 text-text-secondary text-sm font-normal">Réf.</th>
                <th className="text-left p-4 text-text-secondary text-sm font-normal">Nom (EN)</th>
                <th className="text-left p-4 text-text-secondary text-sm font-normal">Nom (FR)</th>
                <th className="text-left p-4 text-text-secondary text-sm font-normal">Dimensions</th>
                <th className="text-left p-4 text-text-secondary text-sm font-normal">Statut</th>
                <th className="text-right p-4 text-text-secondary text-sm font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    {product.image ? (
                      <img src={product.image} alt={product.name_en} className="w-12 h-12 object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-surface-tertiary flex items-center justify-center">
                        <Image size={20} className="text-text-muted" />
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-text-primary font-mono text-sm">{product.reference}</td>
                  <td className="p-4 text-text-primary">{product.name_en}</td>
                  <td className="p-4 text-text-secondary">{product.name_fr}</td>
                  <td className="p-4 text-text-secondary text-sm">{product.dimensions || '-'}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs ${product.active ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
                      {product.active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => handleEdit(product)}
                      className="p-2 text-text-secondary hover:text-brand-gold transition-colors"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="p-2 text-text-secondary hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-text-muted">
                    Aucun produit. Cliquez sur "Charger défauts" ou "Ajouter" pour commencer.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-secondary border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-white/10">
              <h2 className="font-playfair text-xl text-text-primary">
                {editingProduct ? 'Modifier le produit' : 'Ajouter un produit'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-text-secondary hover:text-text-primary">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Référence *</label>
                  <input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData({...formData, reference: e.target.value})}
                    placeholder="ex: P02"
                    className="w-full bg-surface-tertiary border border-white/10 text-text-primary px-4 py-2 focus:outline-none focus:border-brand-gold/50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Dimensions</label>
                  <input
                    type="text"
                    value={formData.dimensions}
                    onChange={(e) => setFormData({...formData, dimensions: e.target.value})}
                    placeholder="ex: 25/30/35 CM"
                    className="w-full bg-surface-tertiary border border-white/10 text-text-primary px-4 py-2 focus:outline-none focus:border-brand-gold/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Nom (Anglais) *</label>
                  <input
                    type="text"
                    value={formData.name_en}
                    onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                    className="w-full bg-surface-tertiary border border-white/10 text-text-primary px-4 py-2 focus:outline-none focus:border-brand-gold/50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Nom (Français) *</label>
                  <input
                    type="text"
                    value={formData.name_fr}
                    onChange={(e) => setFormData({...formData, name_fr: e.target.value})}
                    className="w-full bg-surface-tertiary border border-white/10 text-text-primary px-4 py-2 focus:outline-none focus:border-brand-gold/50"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Description (Anglais) *</label>
                <textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                  rows={2}
                  className="w-full bg-surface-tertiary border border-white/10 text-text-primary px-4 py-2 focus:outline-none focus:border-brand-gold/50"
                  required
                />
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Description (Français) *</label>
                <textarea
                  value={formData.description_fr}
                  onChange={(e) => setFormData({...formData, description_fr: e.target.value})}
                  rows={2}
                  className="w-full bg-surface-tertiary border border-white/10 text-text-primary px-4 py-2 focus:outline-none focus:border-brand-gold/50"
                  required
                />
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">URL de l'image *</label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  placeholder="https://..."
                  className="w-full bg-surface-tertiary border border-white/10 text-text-primary px-4 py-2 focus:outline-none focus:border-brand-gold/50"
                  required
                />
                {formData.image && (
                  <img src={formData.image} alt="Preview" className="mt-2 w-20 h-20 object-cover" />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Ordre d'affichage</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                    className="w-full bg-surface-tertiary border border-white/10 text-text-primary px-4 py-2 focus:outline-none focus:border-brand-gold/50"
                  />
                </div>
                <div className="flex items-center pt-7">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({...formData, active: e.target.checked})}
                      className="w-5 h-5 accent-brand-gold"
                    />
                    <span className="text-text-primary">Produit actif</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary inline-flex items-center gap-2">
                  <Save size={18} />
                  {editingProduct ? 'Mettre à jour' : 'Créer'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
